import {
	ClientSideDataGridModel,
	ClientSideDataGridModelRenderProps,
} from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import {
	booleanColumn,
	dateColumn,
	dateTimeColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	moneyColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import { CellWithRemarkIndicator } from "src/components/common/dataGrid/cells/CellWithRemarkIndicator.tsx";
import i18n from "i18next";
import { getCustomerOrderLineStateLabels } from "src/api/generated/erp/db/types/enums/customerOrderLineState.ts";
import { getSalesPartTypeLabels } from "src/api/generated/erp/db/types/enums/salesPartType.ts";
import { getAcquisitionMethodLabels } from "src/api/generated/erp/db/types/enums/acquisitionMethod.ts";
import { CustomerOrderLinesDataGridContextMenuComponents } from "src/components/views/erp/sales/customerOrderLine/CustomerOrderLinesDataGridContextMenuComponents.tsx";
import { CustomerOrderLinesDataGridActionBarComponents } from "src/components/views/erp/sales/customerOrderLine/CustomerOrderLinesDataGridActionBarComponents.tsx";
import { FormCrudDataGrid, FormCrudDataGridModelProps } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { CustomerOrderLineForm } from "src/components/views/erp/sales/customerOrderLine/CustomerOrderLineForm.tsx";
import {
	CustomerOrderLineDataGridApi,
	CustomerOrderLineDataGridApi_CustomerOrderLineDto,
	CustomerOrderLineDataGridApi_Data,
} from "src/api/generated/erp/sales/customerOrder/api/customerOrderLineDataGridApi.ts";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { ImportDocumentsToCustomerOrderLinesButton } from "src/components/views/erp/sales/customerOrderLine/utils/ImportDocumentsToCustomerOrderLinesButton.tsx";
import { GridRowParams } from "@mui/x-data-grid/models";
import { aavoGridDetailPanelToggleCol } from "src/components/common/dataGrid/masterDetail/aavoGridDetailPanelToggleCol.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { StaticDataGridModel } from "src/components/common/dataGrid/gridModel/StaticDataGridModel.tsx";
import { useState } from "react";
import { getOnlyMember } from "src/utils/arrayUtils.ts";
import { ImportCustomerOrderLinesFromTabularDataButton } from "src/components/views/erp/sales/customerOrderLine/utils/ImportCustomerOrderLinesFromTabularDataButton.tsx";
import { ImportFromVertexIndButton } from "src/components/views/erp/integrations/vertexInd/ImportFromVertexIndButton.tsx";
import { VertexToCustomerOrderLineApi } from "src/api/generated/erp/sales/customerOrder/importing/vertexToCustomerOrderLineApi.ts";
import { formatLabelWithCurrencyCode, renderMoneyCellWithCurrency } from "src/components/views/erp/sales/salesUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerOrderLinesDataGridProps {
	customerOrderId: number;
	parentLineId: number | null;
	hideActionBar?: boolean;
	refreshRef?: RefreshableElementRef;
	showReleaseButton?: boolean;
	editable?: boolean;
}

export interface CustomerOrderLinesFilterParams {
	searchQuery: string;
}

interface FetchParams {
	searchQuery: string;
	showCancelledLines: boolean;
}

export const CustomerOrderLinesDataGrid = (props: CustomerOrderLinesDataGridProps) => {
	const { customerOrderId, parentLineId, refreshRef } = props;

	// Represents all selected rows, regardless if they are in master or detail data grid.
	const [globalSelectedRows, setGlobalSelectedRows] = useState<CustomerOrderLineDataGridApi_CustomerOrderLineDto[]>(
		[],
	);
	const localSelectedRows = globalSelectedRows.filter((row) => row.parentLineId == parentLineId);

	return (
		<ClientSideDataGridModel
			refreshRef={refreshRef}
			gridId={"3ADC38DC6635BEF8"}
			fetchData={(params) =>
				CustomerOrderLineDataGridApi.getData({
					customerOrderId: customerOrderId,
					parentLineId: parentLineId,
					...params,
				})
			}
			getRows={(data) => data.customerOrderLines}
			getRowId={(row) => row.customerOrderLineId}
			initialParams={{
				searchQuery: "",
				showCancelledLines: false,
			}}
			selectedRows={localSelectedRows}
			onSelectionChanged={setGlobalSelectedRows}
			render={(params) => (
				<CustomerOrderLinesDataGridInner
					{...props}
					{...params}
					globalSelectedRows={globalSelectedRows}
					setGlobalSelectedRows={setGlobalSelectedRows}
				/>
			)}
		/>
	);
};

interface CustomerOrderLinesDataGridInnerProps
	extends
		CustomerOrderLinesDataGridProps,
		Omit<
			ClientSideDataGridModelRenderProps<
				CustomerOrderLineDataGridApi_Data,
				CustomerOrderLineDataGridApi_CustomerOrderLineDto,
				FetchParams
			>,
			"dataGridProps"
		> {
	dataGridProps: FormCrudDataGridModelProps<CustomerOrderLineDataGridApi_CustomerOrderLineDto>;
	globalSelectedRows: CustomerOrderLineDataGridApi_CustomerOrderLineDto[];
	setGlobalSelectedRows: (rows: CustomerOrderLineDataGridApi_CustomerOrderLineDto[]) => void;
}

const CustomerOrderLinesDataGridInner = (props: CustomerOrderLinesDataGridInnerProps) => {
	const {
		dataGridProps,
		hideActionBar,
		data,
		parentLineId,
		customerOrderId,
		refreshData,
		currentParams,
		globalSelectedRows,
		showReleaseButton = true,
		editable = true,
	} = props;
	const { tenantConfig } = useTenantCustomizations();

	const { customerOrderSummary, customerOrder } = data;
	const sellingCurrencyCode = customerOrderSummary.currencyCode;
	const domesticCurrencyCode = customerOrderSummary.domesticCurrencyCode;

	return (
		<FormCrudDataGrid<CustomerOrderLineDataGridApi_CustomerOrderLineDto>
			hideActionBar={hideActionBar}
			columns={[
				aavoGridDetailPanelToggleCol<CustomerOrderLineDataGridApi_CustomerOrderLineDto>(
					(row) => row.childLines.length > 0,
				),
				integerColumn({
					field: "lineNumber",
					headerName: i18n.t("number_shortened"),
					width: 50,
				}),
				textColumn({
					field: "configurationOrPartDescription",
					headerName: i18n.t("part"),
					width: 250,
					renderCell: ({ row }) => (
						<CellWithRemarkIndicator value={row.configurationOrPartDescription} remark={row.partRemark} />
					),
				}),
				floatColumn({
					field: "salesQuantityAsSalesUnits",
					headerName: i18n.t("ordered_quantity"),
				}),
				textColumn({
					field: "salesUnit",
					headerName: i18n.t("sales_unit"),
				}),
				textColumn({
					field: "mark",
					headerName: i18n.t("mark"),
					width: 200,
				}),
				parentLineId == null && [
					dateColumn({
						field: "plannedDeliveryDate",
						headerName: i18n.t("planned_delivery_date"),
						width: 160,
					}),
					moneyColumn({
						field: "unitPrice",
						headerName: formatLabelWithCurrencyCode(i18n.t("unit_price"), sellingCurrencyCode),
						width: 90,
						renderCell: renderMoneyCellWithCurrency(sellingCurrencyCode),
					}),
					moneyColumn({
						field: "unitPriceWithVat",
						headerName: formatLabelWithCurrencyCode(i18n.t("unit_price_with_vat"), sellingCurrencyCode),
						width: 150,
						renderCell: renderMoneyCellWithCurrency(sellingCurrencyCode),
					}),
					floatColumn({
						field: "discountPercentage",
						headerName: i18n.t("discount_percentage"),
						width: 80,
					}),
					moneyColumn({
						field: "totalPrice",
						headerName: formatLabelWithCurrencyCode(i18n.t("total_price"), sellingCurrencyCode),
						renderCell: renderMoneyCellWithCurrency(sellingCurrencyCode),
					}),
					moneyColumn({
						field: "totalPriceWithVat",
						headerName: formatLabelWithCurrencyCode(i18n.t("total_price_with_vat"), sellingCurrencyCode),
						width: 155,
						renderCell: renderMoneyCellWithCurrency(sellingCurrencyCode),
					}),
					textColumn({
						field: "salesPriceUnit",
						headerName: i18n.t("sales_price_unit"),
					}),
				],
				enumColumn({
					field: "customerOrderLineState",
					headerName: i18n.t("state"),
					enumLabels: getCustomerOrderLineStateLabels(),
				}),
				tenantConfig.erp.configuratorEnabled &&
					booleanColumn({
						field: "isAwaitingConfiguration",
						headerName: i18n.t("awaiting_configuration"),
						width: 155,
					}),
				enumColumn({
					field: "salesPartType",
					headerName: i18n.t("type"),
					enumLabels: getSalesPartTypeLabels(),
					width: 75,
				}),
				enumColumn({
					field: "acquisitionMethod",
					headerName: i18n.t("acquisition_method"),
					enumLabels: getAcquisitionMethodLabels(),
					width: 125,
				}),
				textColumn({
					field: "defaultProductionLineName",
					headerName: i18n.t("default_production_line"),
				}),
				textColumn({
					field: "shopOrderBatchCode",
					headerName: i18n.t("shop_order_batch"),
				}),
				textColumn({
					field: "capacityQuantity",
					headerName: i18n.t("capacity_quantity"),
					width: 125,
				}),
				moneyColumn({
					field: "unitCost",
					headerName: formatLabelWithCurrencyCode(i18n.t("unit_cost"), domesticCurrencyCode),
					width: 125,
					renderCell: renderMoneyCellWithCurrency(domesticCurrencyCode),
				}),
				moneyColumn({
					field: "totalCost",
					headerName: formatLabelWithCurrencyCode(i18n.t("total_cost"), domesticCurrencyCode),
					width: 135,
					renderCell: renderMoneyCellWithCurrency(domesticCurrencyCode),
				}),
				parentLineId == null &&
					textColumn({
						field: "vatCodeName",
						headerName: i18n.t("vat_code"),
					}),
				dateTimeColumn({
					field: "releaseDate",
					headerName: i18n.t("released"),
					width: 130,
				}),
				textColumn({
					field: "partUnit",
					headerName: i18n.t("warehouse_unit"),
					width: 105,
				}),
				textColumn({
					field: "purchaseUnit",
					headerName: i18n.t("purchase_unit"),
					width: 90,
				}),
				dateColumn({
					field: "lastPickingDate",
					headerName: i18n.t("last_picking_date"),
					width: 155,
				}),
				dateColumn({
					field: "lastShippingDate",
					headerName: i18n.t("last_shipping_date"),
					width: 150,
				}),
				floatColumn({
					field: "reservedQuantity",
					headerName: i18n.t("reserved"),
					width: 60,
				}),
				floatColumn({
					field: "pickedQuantity",
					headerName: i18n.t("picked"),
					width: 65,
				}),
				floatColumn({
					field: "deliveredQuantity",
					headerName: i18n.t("delivered"),
					width: 70,
				}),
				floatColumn({
					field: "invoicedQuantity",
					headerName: i18n.t("invoiced"),
					width: 75,
				}),
				integerColumn({
					field: "customerOrderLineId",
					headerName: i18n.t("id"),
					width: 75,
				}),
			]}
			actionBarComponents={
				<CustomerOrderLinesDataGridActionBarComponents
					parentLineId={parentLineId}
					globalSelectedRows={globalSelectedRows}
					refreshData={refreshData}
					customerOrderSummary={customerOrderSummary}
				/>
			}
			actionBarMenuComponents={[
				<MenuCheckbox
					key={"showCancelled"}
					label={i18n.t("show_cancelled_items")}
					checked={currentParams.showCancelledLines}
					onChange={(checked) => refreshData({ showCancelledLines: checked })}
				/>,
				<ImportDocumentsToCustomerOrderLinesButton
					key={"importDocumentsToCustomerOrderLinesButton"}
					customerOrderId={customerOrderId}
				/>,
				<ImportCustomerOrderLinesFromTabularDataButton
					key={"importCustomerOrderLinesFromTabularDataButton"}
					customerOrderId={customerOrderId}
					afterImport={refreshData}
				/>,
				tenantConfig.erp.customerOrderLineImportMethods.includes("VERTEX") && (
					<ImportFromVertexIndButton
						key={"importFromVertex"}
						submit={async (fileHandle) => {
							await VertexToCustomerOrderLineApi.importVertexFileToCustomerOrderLines({
								customerOrderId: customerOrderId,
								fileHandle: fileHandle,
							});
							await refreshData();
						}}
					/>
				),
			]}
			rowContextMenuComponents={({ row, allSelectedRows, onlySingleRowSelected, multipleRowsSelected }) => (
				<CustomerOrderLinesDataGridContextMenuComponents
					key={"contextMenu"}
					row={row}
					allSelectedRows={allSelectedRows}
					onlySingleRowSelected={onlySingleRowSelected}
					multipleRowsSelected={multipleRowsSelected}
					refreshData={refreshData}
					parentLineId={parentLineId}
					customerOrder={customerOrder}
					showReleaseButton={showReleaseButton}
					editable={editable}
				/>
			)}
			form={{
				editEnabled: editable,
				addRowEnabled: editable && !getCustomerOrderIsDelivered(),
				dialogSize: "lg",
				dialogTitle: i18n.t("customer_order_line"),
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.sales.customerOrderLine.formDialog"),
					pathSegments:
						row == null ? undefined : [`${i18n.t("customer_order_line")} ${row.customerOrderLineId}`],
					data: {
						customerOrderId,
						customerOrderLineId: row?.customerOrderLineId ?? null,
						parentCustomerOrderLineId: row?.parentLineId ?? parentLineId,
					},
				}),
				component: ({ row, ...other }) => (
					<CustomerOrderLineForm
						customerOrderId={customerOrderId}
						customerOrderLineId={row?.customerOrderLineId}
						parentLineId={(row != null ? row?.parentLineId : parentLineId) ?? undefined}
						{...other}
					/>
				),
				customEditAction: ({ openFormForRow }) => {
					const rowToEdit = getOnlyMember(globalSelectedRows);
					if (rowToEdit == null) {
						return {
							type: "disabled",
							tooltip: i18n.t("select_a_single_row"),
						};
					}
					return {
						type: "enabled",
						action: () => {
							openFormForRow(rowToEdit);
						},
					};
				},
			}}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.customerOrderLines.grid"),
				data: {
					customerOrderId,
					parentCustomerOrderLineId: parentLineId,
				},
				selectedRowPathSegment: (row) => `${i18n.t("customer_order_line")} ${row.customerOrderLineId}`,
				rowFields: ["customerOrderLineId", "customerOrderId", "parentLineId", "partId", "mark"],
			}}
			getDetailPanelContent={(params) => <DetailPanel {...props} {...params} />}
			{...dataGridProps}
		/>
	);

	function getCustomerOrderIsDelivered() {
		return ["DELIVERED", "INVOICED", "CANCELLED"].includes(customerOrder.customerOrderState);
	}
};

interface DetailPanelContentProps
	extends CustomerOrderLinesDataGridInnerProps, GridRowParams<CustomerOrderLineDataGridApi_CustomerOrderLineDto> {}

const DetailPanel = ({
	row: parentRow,
	data,
	refreshData,
	currentParams,
	customerOrderId,
	globalSelectedRows,
	setGlobalSelectedRows,
	showReleaseButton,
}: DetailPanelContentProps) => {
	if (parentRow.childLines.length === 0) return null;

	const childLinesMapped: CustomerOrderLineDataGridApi_CustomerOrderLineDto[] = parentRow.childLines.map(
		(childLine) => ({
			...childLine,
			childLines: [],
		}),
	);

	const selectedRowsForThisPackage = globalSelectedRows.filter(
		(row) => row.parentLineId == parentRow.customerOrderLineId,
	);

	return (
		<HorizontalBox
			sx={{
				margin: 2,
				marginLeft: 6,
			}}
		>
			<StaticDataGridModel
				rows={childLinesMapped}
				gridId={"7ADC38DC6635BEF6"}
				getRowId={(row) => row.customerOrderLineId}
				selectedRows={selectedRowsForThisPackage}
				onSelectionChanged={setGlobalSelectedRows}
				render={({ dataGridProps, getApi, selectedRows, onlySelectedRow, setSelectedRows }) => (
					<CustomerOrderLinesDataGridInner
						data={{
							...data,
							customerOrderLines: childLinesMapped,
						}}
						currentParams={currentParams}
						refreshData={refreshData}
						globalSelectedRows={globalSelectedRows}
						setGlobalSelectedRows={setGlobalSelectedRows}
						selectedRows={selectedRows}
						onlySelectedRow={onlySelectedRow}
						setSelectedRows={setSelectedRows}
						hideActionBar
						customerOrderId={customerOrderId}
						parentLineId={parentRow.customerOrderLineId}
						getApi={getApi}
						showReleaseButton={showReleaseButton}
						dataGridProps={{
							...dataGridProps,
							refreshData: refreshData,
						}}
					/>
				)}
			/>
		</HorizontalBox>
	);
};
